import { Icon, Stack, Tooltip } from '@efast_public/fjd-component-library';
import type {
  IconProps,
  BadgeProps
} from '@efast_public/fjd-component-library';
import { AntragObj } from '../../api/models/Antrag/AntragsModel';
import BearbeitungsstandBadge from '../shared/BearbeitungsstandBadge/BearbeitungsstandBadge';
import BescheidstatusBadge from '../shared/BescheidstatusBadge/BescheidstatusBadge';
import { Bearbeitungsstand } from '../../api/models/Status/Bearbeitungsstand';
import { Bescheidstatus } from '../../api/models/Status/Bescheidstatus';

export interface statusConfig {
  withStatus?: boolean;
  statusIntent?: 'negative' | 'positive' | 'neutral';
  statusLabel?: string;
  statusIcon?: IconProps['name'];
  iconAppearance?: IconProps['variant'];
  iconSize?: BadgeProps['size'];
  glyph?: IconProps['name'];
}

export const statusCell = (
  bearbeitungsstand: Bearbeitungsstand,
  bescheidstatus: Bescheidstatus,
  bescheidUeberfaellig?: boolean
): JSX.Element | null => {
  const statusElement = getStatusElement(bearbeitungsstand, bescheidstatus);

  if (!statusElement) {
    return null;
  }

  if (!bescheidUeberfaellig) {
    return statusElement;
  }

  return (
    <Stack direction="row" spacing="xs">
      {statusElement}
      <Tooltip
        placement="auto"
        tooltip={<div>Versandbestätigung dauert unverhältnismäßig lange</div>}
      >
        <div id="bescheid-ueberfaellig-status">
          <Icon variant="warning" name="alarm" size="md" />
        </div>
      </Tooltip>
    </Stack>
  );
};

const getStatusElement = (
  bearbeitungsstand: Bearbeitungsstand,
  bescheidstatus: Bescheidstatus
): JSX.Element | null => {
  switch (bearbeitungsstand) {
    case Bearbeitungsstand.OFFEN:
      if (bescheidstatus === Bescheidstatus.NOCHNICHTBESCHIEDEN) {
        return (
          <Tooltip
            placement="auto"
            tooltip={<div>Offen, noch nicht beschieden</div>}
          >
            <div id="circle-dash-default-status">
              <Icon name="circle-dash" size="md" />
            </div>
          </Tooltip>
        );
      }
      break;
    case Bearbeitungsstand.INBEARBEITUNG:
      if (bescheidstatus === Bescheidstatus.NOCHNICHTBESCHIEDEN) {
        return (
          <Tooltip
            placement="auto"
            tooltip={<div>In Bearbeitung, noch nicht beschieden</div>}
          >
            <div id="incomplete-default-status">
              <Icon name="incomplete" size="md" />
            </div>
          </Tooltip>
        );
      }
      if (bescheidstatus === Bescheidstatus.POSITIV) {
        return (
          <Tooltip
            placement="auto"
            tooltip={<div>In Bearbeitung, positiv beschieden</div>}
          >
            <div id="checkmark-filled-success-status">
              <Icon variant="success" name="checkmark-filled" size="md" />
            </div>
          </Tooltip>
        );
      }
      if (bescheidstatus === Bescheidstatus.NEGATIV) {
        return (
          <Tooltip
            placement="auto"
            tooltip={<div>In Bearbeitung, negativ beschieden</div>}
          >
            <div id="close-filled-error-status">
              <Icon variant="error" name="error-filled" size="md" />
            </div>
          </Tooltip>
        );
      }
      break;
    case Bearbeitungsstand.ABGESCHLOSSEN:
      const bescheid: string =
        bescheidstatus === Bescheidstatus.POSITIV ? 'positiv' : 'negativ';
      return (
        <Tooltip
          placement="auto"
          tooltip={<div>Abgeschlossen, {bescheid} beschieden</div>}
        >
          <div id="locked-negative-status">
            <Icon name="locked" size="md" variant="secondary" />
          </div>
        </Tooltip>
      );
    default:
      return null;
  }
  return null;
};

export const tabStatusConfig = (
  bearbeitungsstand: Bearbeitungsstand,
  bescheidstatus: Bescheidstatus
): statusConfig | null => {
  switch (bearbeitungsstand) {
    case Bearbeitungsstand.OFFEN:
      if (bescheidstatus === Bescheidstatus.NOCHNICHTBESCHIEDEN) {
        return {
          withStatus: false,
          iconAppearance: 'primary',
          glyph: 'circle-dash',
          iconSize: 'md'
        };
      }
      break;
    case Bearbeitungsstand.INBEARBEITUNG:
      if (bescheidstatus === Bescheidstatus.NOCHNICHTBESCHIEDEN) {
        return {
          withStatus: false,
          iconAppearance: 'primary',
          glyph: 'incomplete',
          iconSize: 'md'
        };
      }
      if (bescheidstatus === Bescheidstatus.POSITIV) {
        return {
          withStatus: false,
          iconAppearance: 'success',
          glyph: 'checkmark-filled',
          iconSize: 'md'
        };
      }
      if (bescheidstatus === Bescheidstatus.NEGATIV) {
        return {
          withStatus: false,
          iconAppearance: 'error',
          glyph: 'error-filled',
          iconSize: 'md'
        };
      }
      break;
    case Bearbeitungsstand.ABGESCHLOSSEN:
      return {
        withStatus: true,
        statusIntent: 'negative',
        statusLabel: '',
        glyph: 'locked',
        statusIcon: 'locked'
      };
    default:
      return null;
  }
  return null;
};

export const bearbeitungsstandCell = (
  antrag: AntragObj
): JSX.Element | null => {
  const bearbeitungsstand: Bearbeitungsstand = antrag.bearbeitungsstand;

  return (
    <BearbeitungsstandBadge
      bearbeitungsstand={bearbeitungsstand}
      fjdBadgeSize={'sm'}
    />
  );
};

export const bescheidstatusCell = (antrag: AntragObj): JSX.Element | null => {
  const bescheidstatus: Bescheidstatus = antrag.bescheidstatus;
  const bearbeitungsstand: Bearbeitungsstand = antrag.bearbeitungsstand;

  return (
    <Stack direction="row" spacing="xs">
      <BescheidstatusBadge
        bescheidstatus={bescheidstatus}
        bearbeitungsstand={bearbeitungsstand}
        fjdBadgeSize={'sm'}
      />
      {antrag.bescheidUeberfaellig && (
        <Tooltip
          placement="auto"
          tooltip={<div>Versandbestätigung dauert unverhältnismäßig lange</div>}
        >
          <div id="bescheid-ueberfaellig-status">
            <Icon variant="warning" name="alarm" size="md" />
          </div>
        </Tooltip>
      )}
    </Stack>
  );
};

export const rueckkanalCell = (
  antrag: AntragObj | undefined
): JSX.Element | null => {
  return (
    <Stack direction="row" spacing="xs" alignItems="center">
      {antrag?.canCommunicate ? (
        <Icon
          id="checkmark-filled"
          variant="success"
          name="checkmark-filled"
          size="md"
        />
      ) : (
        <Icon id="error-filled" variant="error" name="error-filled" size="md" />
      )}
      {antrag?.isEBescheidConsentGiven ? (
        <></>
      ) : (
        <Tooltip
          placement="auto"
          tooltip={'Keine eBescheid-Einwilligung vorhanden.'}
        >
          <div id="e-bescheid-consent-missing">
            <Icon
              id="information-filled"
              variant="info"
              name="information-filled"
              size="md"
            />
          </div>
        </Tooltip>
      )}
    </Stack>
  );
};
